import { useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { appContext, AppContext } from '../../../context/AppContext';
import { Product, ProductDraft, SearchFilter, ProductOrderBy } from '@anticatrade/shared';
import { ProductsResponse } from '@anticatrade/admin';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

const PRODUCT_LIMIT_STORAGE_KEY = 'anticatrade_admin_product_limit';
const PRODUCT_ORDER_BY_STORAGE_KEY = 'anticatrade_admin_product_orderby';
const DEFAULT_LIMIT = 20;
const DEFAULT_ORDER_BY: ProductOrderBy = 'datedesc';
const POLL_INTERVAL_MS = 7000;
const SEARCH_DEBOUNCE_MS = 400;

const getStoredLimit = (): number => {
	const stored = localStorage.getItem(PRODUCT_LIMIT_STORAGE_KEY);
	if (stored != null) {
		const n = Number(stored);
		if (!isNaN(n)) {
			return n;
		}
		localStorage.removeItem(PRODUCT_LIMIT_STORAGE_KEY);
	}
	return DEFAULT_LIMIT;
};

const getStoredOrderBy = (): ProductOrderBy => {
	const stored = localStorage.getItem(PRODUCT_ORDER_BY_STORAGE_KEY);
	if (stored != null) {
		const valid: ProductOrderBy[] = [
			'popularity',
			'pricedesc',
			'priceasc',
			'datedesc',
			'dateasc',
		];
		if (valid.includes(stored as ProductOrderBy)) {
			return stored as ProductOrderBy;
		}
		// The value is not valid. Remove it from the storage and use
		// the default order.
		localStorage.removeItem(PRODUCT_ORDER_BY_STORAGE_KEY);
	}
	return DEFAULT_ORDER_BY;
};

const useProducts = () => {
	const [products, setProducts] = useState<Product[] | null>();
	const [count, setCount] = useState<number>();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [page, setPage] = useState<number>(1);
	const [limit, setLimit] = useState<number>(getStoredLimit());
	const [drafts, setDrafts] = useState<ProductDraft[]>([]);
	const [deletingDraftIds, setDeletingDraftIds] = useState<Set<number>>(new Set());
	const [searchInput, setSearchInput] = useState<string>('');
	const [orderBy, setOrderBy] = useState<ProductOrderBy>(getStoredOrderBy);

	const { api, handleError } = useContext(appContext as React.Context<AppContext>);
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const location = useLocation();

	const limitRef = useRef<number>(getStoredLimit());
	const pollIntervalRef = useRef<ReturnType<typeof setInterval>>();
	const debounceRef = useRef<ReturnType<typeof setTimeout>>();
	const isInputSearchRef = useRef<boolean>(false);
	const isOrderBySearchRef = useRef<boolean>(false);
	const isFirstFetchRef = useRef<boolean>(true);

	const processingIDs = useMemo(() => {
		if (products == null || products.length === 0) {
			return [];
		}
		const ids = [];
		for (const p of products) {
			// Failed products retain pending images so the merchant can replace them.
			// Those pending rows no longer indicate active processing.
			if (
				p.status !== 'failed' &&
				(p.status === 'processing' || p.imageReprocessing)
			) {
				ids.push(p.id);
			}
		}
		return ids;
	}, [products]);

	useEffect(() => {
		if (location.hash) {
			// The hash is used to scroll back over a product after
			// having closed its page.
			return;
		}
		const body = document.querySelector('body');
		body?.scrollTo({
			top: 0,
		});
	}, [page, orderBy]);

	useEffect(() => {
		if (isInputSearchRef.current || isOrderBySearchRef.current) {
			return;
		}

		let pa = page;
		const queryPage = searchParams.get('page');
		if (queryPage != null) {
			const p = Number(queryPage);
			if (!isNaN(p) && p !== 0) {
				setPage(p);
				pa = p;
			}
		} else {
			setPage(1);
			pa = 1;
		}

		let lim = limit;
		if (limit !== limitRef.current) {
			// if limit has changed, navigate to first page to avoid
			// out of range offsets.
			limitRef.current = limit;
			localStorage.setItem(PRODUCT_LIMIT_STORAGE_KEY, String(limit));
			navigate('/products?page=1');
			pa = 1;
		}

		fetchProducts(
			pa,
			lim,
			searchInput ? { search: searchInput } : undefined,
			orderBy ? orderBy : undefined,
		);
	}, [page, limit, searchParams]);

	useEffect(() => {
		const fetchDrafts = async () => {
			try {
				const res = await api.productDrafts();
				setDrafts(res.drafts);
			} catch {
				// Non-critical, ignore errors.
			}
		};
		fetchDrafts();
	}, []);

	const updateProductPublications = useCallback(
		(productId: number, publications: Product['publications']) => {
			setProducts(
				(prev) =>
					prev?.map((p) => (p.id === productId ? { ...p, publications } : p)) ?? prev,
			);
		},
		[],
	);

	const deleteDraft = useCallback(
		async (id: number) => {
			setDeletingDraftIds((prev) => new Set(prev).add(id));
			try {
				await api.deleteProductDraft(id);
				setDrafts((prev) => prev.filter((d) => d.id !== id));
			} catch (err) {
				handleError(err as Error);
			} finally {
				setDeletingDraftIds((prev) => {
					const next = new Set(prev);
					next.delete(id);
					return next;
				});
			}
		},
		[api],
	);

	const fetchProducts = useCallback(
		async (page: number, limit: number, filter?: SearchFilter, orderBy?: ProductOrderBy) => {
			setIsLoading(true);
			let res: ProductsResponse;
			try {
				res = await api.products(page, limit, undefined, filter, orderBy);
			} catch (err) {
				if (isInputSearchRef.current) {
					isInputSearchRef.current = false;
				}
				if (isOrderBySearchRef.current) {
					isOrderBySearchRef.current = false;
				}
				if (isFirstFetchRef.current) {
					isFirstFetchRef.current = false;
				}
				setIsLoading(false);
				handleError(err as Error);
				return;
			}
			if (isInputSearchRef.current) {
				isInputSearchRef.current = false;
			}
			if (isOrderBySearchRef.current) {
				isOrderBySearchRef.current = false;
			}
			if (isFirstFetchRef.current) {
				isFirstFetchRef.current = false;
			}
			setProducts(res.products);
			setCount(res.count);
			setIsLoading(false);
		},
		[api],
	);

	useEffect(() => {
		if (processingIDs.length === 0) {
			if (pollIntervalRef.current != null) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = undefined;
			}
			return;
		}

		pollIntervalRef.current = setInterval(async () => {
			try {
				const res = await api.products(1, processingIDs.length, processingIDs);
				setProducts((prev) => {
					if (prev == null) {
						return prev;
					}
					let changed = false;
					const updated: Product[] = [];
					for (const oldP of prev) {
						const newP = res.products?.find((r) => r.id === oldP.id);
						if (
							newP != null &&
							(newP.status === 'failed' ||
								(newP.status !== 'processing' && !newP.imageReprocessing))
						) {
							changed = true;
							updated.push({
								...oldP,
								status: newP.status,
								processingError: newP.processingError,
								imageReprocessing: newP.imageReprocessing,
								image: newP.image,
								gallery: newP.gallery,
							});
						} else {
							updated.push(oldP);
						}
					}
					return changed ? updated : prev;
				});
			} catch {
				// Ignore errors during polling.
			}
		}, POLL_INTERVAL_MS);

		return () => {
			if (pollIntervalRef.current != null) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = undefined;
			}
		};
	}, [processingIDs]);

	useEffect(() => {
		if (isFirstFetchRef.current) {
			return;
		}

		if (debounceRef.current != null) {
			clearTimeout(debounceRef.current);
		}

		debounceRef.current = setTimeout(() => {
			isInputSearchRef.current = true;
			setPage(1);
			navigate('/products?page=1', { replace: true });
			fetchProducts(
				1,
				limitRef.current,
				searchInput ? { search: searchInput } : undefined,
				orderBy ? orderBy : undefined,
			);
		}, SEARCH_DEBOUNCE_MS);

		return () => {
			if (debounceRef.current != null) {
				clearTimeout(debounceRef.current);
			}
		};
	}, [searchInput]);

	const onOrderByChange = (newOrderBy: ProductOrderBy) => {
		setOrderBy(newOrderBy);
		localStorage.setItem(PRODUCT_ORDER_BY_STORAGE_KEY, newOrderBy);
		if (debounceRef.current != null) {
			clearTimeout(debounceRef.current);
		}
		isOrderBySearchRef.current = true;
		setPage(1);
		navigate('/products?page=1', { replace: true });
		fetchProducts(
			1,
			limitRef.current,
			searchInput ? { search: searchInput } : undefined,
			newOrderBy,
		);
	};

	return {
		isLoading,
		products,
		count,
		page,
		limit,
		setLimit,
		drafts,
		deleteDraft,
		deletingDraftIds,
		searchInput,
		setSearchInput,
		orderBy,
		onOrderByChange,
		updateProductPublications,
	};
};

export { useProducts };
